import React, { useEffect, useMemo, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import SlideOutSidebar from "ui-components/slide-out-sidebar/slideOutSidebar";
import Button, { ButtonThemes } from "ui-components/button/button";
import Table from "ui-components/table/table";
import LabeledTextInput from "ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import LabeledDateRangeInput from "ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import LabeledSelectInput from "ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import {
  formatActionLabel,
  formatDetailsColumn,
  InboundChangeActionType,
  InboundChangeLogEntry,
} from "app/data/inbound-freight/inboundChangeLogModels";
import { useInboundChangeLog } from "../hooks/useInboundChangeLog";
import "./inboundActivityLog.scss";

type InboundActivityLogProps = {
  show: boolean;
  onClose: () => void;
};

const ACTION_TYPE_OPTIONS: XGSSelectOption[] = [
  { label: "All", value: "" },
  { label: "Assign Door", value: "ASSIGN_DOOR" },
  { label: "Arrive Load", value: "ARRIVE_LOAD" },
];

const renderDetailsCell = (entry: InboundChangeLogEntry) => {
  const details = formatDetailsColumn(entry);
  const reasonMatch = details.match(/^(.*?)(?:,\s*)?(Reason:\s*.+)$/);
  if (reasonMatch?.[2]) {
    const prefix = reasonMatch[1]?.trim();
    return (
      <span>
        {prefix}
        {prefix ? ", " : ""}
        <strong>{reasonMatch[2]}</strong>
      </span>
    );
  }
  return details;
};

const columnHelper = createColumnHelper<InboundChangeLogEntry>();

const columns = [
  columnHelper.accessor("changedAt", {
    id: "changedAt",
    header: "Timestamp",
    cell: (cellProps) => {
      const value = cellProps.getValue();
      return (
        <>
          {value && (
            <div className="xgs-inbound-activity-log__date">
              <div>{(value as any).toUiDateFormat?.() ?? value}</div>
              <div className="xgs-inbound-activity-log__gray-value">
                {(value as any).toUiTimeShortFormat?.() ?? ""}
              </div>
            </div>
          )}
        </>
      );
    },
    size: 140,
    minSize: 120,
  }),
  columnHelper.accessor("modifiedBy", {
    id: "modifiedBy",
    header: "User",
    size: 110,
    minSize: 80,
  }),
  columnHelper.accessor("manifestNumber", {
    id: "manifestNumber",
    header: "Manifest",
    cell: (cellProps) => {
      const manifestNumber = cellProps.getValue();
      return manifestNumber ? String(manifestNumber) : "—";
    },
    size: 120,
    minSize: 90,
  }),
  columnHelper.accessor("actionType", {
    id: "actionType",
    header: "Action",
    cell: (cellProps) => formatActionLabel(cellProps.getValue()),
    size: 120,
    minSize: 100,
  }),
  columnHelper.display({
    id: "details",
    header: "Details",
    cell: (cellProps) => renderDetailsCell(cellProps.row.original),
    size: 280,
    minSize: 180,
  }),
];

/**
 * Inbound Freight generic change audit (Activity Log).
 * GET /inbound-freight/change-log
 */
const InboundActivityLog: React.FC<InboundActivityLogProps> = ({
  show,
  onClose,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateFrom, setDateFrom] = useState<string | undefined>();
  const [dateTo, setDateTo] = useState<string | undefined>();
  const [actionType, setActionType] = useState<XGSSelectOption | null>(
    ACTION_TYPE_OPTIONS[0]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const toApiDate = (value?: string) =>
    value ? (value as any).toApiDateFormat?.() ?? value : undefined;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isError,
  } = useInboundChangeLog(
    {
      search: debouncedSearch || undefined,
      dateFrom: toApiDate(dateFrom),
      dateTo: toApiDate(dateTo),
      actionType: (actionType?.value as InboundChangeActionType) || undefined,
    },
    show
  );

  const rows = useMemo(() => {
    return data?.pages.flatMap((page) => page.content) ?? [];
  }, [data]);

  const onSearch = () => {
    setDebouncedSearch(searchInput.trim());
  };

  const onClearFilters = () => {
    setSearchInput("");
    setDebouncedSearch("");
    setDateFrom(undefined);
    setDateTo(undefined);
    setActionType(ACTION_TYPE_OPTIONS[0]);
  };

  const hasFilters = !!(
    searchInput ||
    dateFrom ||
    dateTo ||
    (actionType?.value && actionType.value !== "")
  );

  return (
    <SlideOutSidebar
      header="Activity Log"
      onClose={onClose}
      show={show}
      size="medium"
      className="xgs-inbound-activity-log"
    >
      <div className="xgs-inbound-activity-log__filters">
        <LabeledTextInput
          label="Search"
          labelMode={LabelModes.column}
          className="xgs-inbound-activity-log__filter-input"
          value={searchInput}
          onChange={(e) => setSearchInput(e.currentTarget.value)}
          onKeyDown={(e) => e.key === "Enter" && onSearch()}
          type="text"
          placeholder="Manifest, user..."
        />
        <LabeledDateRangeInput
          label="Date Range:"
          labelMode={LabelModes.column}
          className="xgs-inbound-activity-log__filter-dates"
          start={dateFrom}
          end={dateTo}
          maxDate={new Date()}
          onStartChange={(v) => setDateFrom(v || undefined)}
          onEndChange={(v) => setDateTo(v || undefined)}
        />
        <LabeledSelectInput
          label="Action Type"
          labelMode={LabelModes.column}
          className="xgs-inbound-activity-log__filter-select"
          options={ACTION_TYPE_OPTIONS}
          value={actionType}
          onValueChange={(v: XGSSelectOption | null | undefined) => {
            setActionType(v ?? ACTION_TYPE_OPTIONS[0]);
          }}
        />
        <Button
          className="xgs-inbound-activity-log__filter-button"
          theme={ButtonThemes.gray}
          onClick={onClearFilters}
          disabled={!hasFilters}
        >
          Clear Filters
        </Button>
        <Button
          className="xgs-inbound-activity-log__filter-button"
          theme={ButtonThemes.blue}
          onClick={onSearch}
        >
          Search
        </Button>
      </div>

      <div className="xgs-inbound-activity-log__table-wrapper">
        {!isError && (
          <Table
            isLoading={isLoading}
            columns={columns}
            data={rows}
            rowHeight={48}
            minTableHeight={200}
            infiniteScroll={true}
            noResultsText="No inbound changes recorded yet"
            infiniteScrollLoading={isFetchingNextPage}
            infiniteScrollHasNext={!!hasNextPage}
            onInfiniteScroll={() => {
              if (hasNextPage) fetchNextPage();
            }}
          />
        )}
      </div>

      {isError && (
        <XGSErrorMessage className="xgs-inbound-activity-log__error">
          Error while obtaining inbound activity log
        </XGSErrorMessage>
      )}
    </SlideOutSidebar>
  );
};

export default InboundActivityLog;
